// src/pages/ProfilesPage.jsx
import React, { useState, useRef, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  TextField,
  IconButton,
  Button,
  Collapse,
  useTheme,
  InputAdornment,
  CircularProgress,
  Pagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import TuneIcon from '@mui/icons-material/Tune';

import { useProfiles } from '../../context/ProfilesContext';
import { ProfileList } from './profiles';
import { FilterManager } from './profiles/filters';

const PROFILES_PER_PAGE = 12;

const ProfilesPage = () => {
  const theme = useTheme();
  const { allProfiles, isLoading } = useProfiles();
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const searchRef = useRef(null);

  // Initialize with all profiles
  useEffect(() => {
    if (allProfiles.length > 0) {
      setFilteredProfiles(allProfiles);
      setCurrentPage(1);
    }
  }, [allProfiles]);

  const handleFiltersChange = (filters) => {
    const hasFilters = Object.values(filters.appliedFilters || {}).some(
      value => value !== null && value !== undefined && value !== ''
    );

    setHasActiveFilters(hasFilters);
    setCurrentPage(1);

    if (filters.results && filters.results.profiles) {
      setFilteredProfiles(filters.results.profiles);
    }
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);

    if (value.trim()) {
      setIsSearching(true);
      setTimeout(() => {
        const filtered = allProfiles.filter(profile =>
          profile.full_name?.toLowerCase().includes(value.toLowerCase()) ||
          profile.city?.toLowerCase().includes(value.toLowerCase()) ||
          profile.job_title?.toLowerCase().includes(value.toLowerCase()) ||
          profile.highest_degree?.toLowerCase().includes(value.toLowerCase()) ||
          profile.religion?.toLowerCase().includes(value.toLowerCase()) ||
          profile.organization?.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredProfiles(filtered);
        setIsSearching(false);
      }, 300);
    } else {
      if (hasActiveFilters) {
        // Keep filter results
      } else {
        setFilteredProfiles(allProfiles);
      }
      setIsSearching(false);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setCurrentPage(1);
    if (hasActiveFilters) {
      // Keep filter results
    } else {
      setFilteredProfiles(allProfiles);
    }
  };

  const handleClearAllFilters = () => {
    setHasActiveFilters(false);
    setFilteredProfiles(allProfiles);
    setSearchQuery('');
    setCurrentPage(1);
  };

  // Determine which profiles to display
  const displayProfiles = searchQuery.trim() || hasActiveFilters ? filteredProfiles : allProfiles;

  // Calculate pagination
  const totalPages = Math.max(1, Math.ceil(displayProfiles.length / PROFILES_PER_PAGE));
  const startIndex = (currentPage - 1) * PROFILES_PER_PAGE;
  const endIndex = Math.min(startIndex + PROFILES_PER_PAGE, displayProfiles.length);
  const paginatedProfiles = displayProfiles.slice(startIndex, endIndex);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography
          variant="h2"
          fontWeight="bold"
          gutterBottom
          sx={{
            color: theme.palette.primary.main,
            fontSize: { xs: '2.5rem', md: '3.5rem' },
            mb: 1
          }}
        >
          find your perfect match
        </Typography>

        <Typography
          variant="h5"
          color="text.secondary"
          gutterBottom
          sx={{ mb: 3 }}
        >
          {displayProfiles.length} verified profiles • 100% confidential
          {displayProfiles.length > PROFILES_PER_PAGE && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Page {currentPage} of {totalPages} ({paginatedProfiles.length} profiles on this page)
            </Typography>
          )}
        </Typography>
      </Box>

      <Box sx={{ position: 'relative', mb: 4 }}>
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 3,
            backgroundColor: theme.palette.primary.main,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <TextField
              fullWidth
              inputRef={searchRef}
              placeholder="Search by name, profession, city, education..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearchChange(searchQuery)}
              variant="filled"
              size="medium"
              InputProps={{
                sx: {
                  borderRadius: 3,
                  backgroundColor: theme.palette.common.white,
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  height: { xs: 56, md: 64 },
                  '&:hover, &.Mui-focused': {
                    backgroundColor: theme.palette.common.white,
                    boxShadow: `0 8px 25px ${theme.palette.action.hover}`
                  },
                  '& .MuiFilledInput-input': {
                    paddingTop: { xs: 16, md: 20 },
                    paddingBottom: { xs: 16, md: 20 }
                  }
                },
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{
                      color: theme.palette.primary.main,
                      fontSize: '1.8rem',
                      ml: 1
                    }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {isSearching ? (
                      <CircularProgress
                        size={24}
                        sx={{ mr: 1 }}
                        color="primary"
                      />
                    ) : searchQuery ? (
                      <IconButton
                        onClick={handleClearSearch}
                        sx={{ mr: -1 }}
                        color="primary"
                      >
                        <ClearIcon />
                      </IconButton>
                    ) : null}
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Paper>
      </Box>

      <Paper
        sx={{
          p: 2,
          mb: 4,
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          overflow: 'visible',
          position: 'relative'
        }}
      >
        <Collapse in={filtersExpanded}>
          <Box sx={{
            width: '100%',
            overflow: 'visible',
            minHeight: 'auto',
            '& > *': {
              width: '100%'
            }
          }}>
            <FilterManager
              profiles={allProfiles}
              onFiltersChange={handleFiltersChange}
            />
          </Box>
        </Collapse>

        {(hasActiveFilters || searchQuery) && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained" // use contained for background
              size="small"
              onClick={handleClearAllFilters}
              sx={{
                borderRadius: 2,
                backgroundColor: "primary.main", // primary background
                color: "paper",          // secondary text
                "&:hover": {
                  backgroundColor: "primary.dark", // darker shade on hover
                },
              }}
            >
              Clear All Filters
            </Button>
          </Box>
        )}
      </Paper>

      <Box>
        {/* Show current page info */}
        {displayProfiles.length > 0 && (
          <Paper
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: theme.palette.primary[100] || "#EDE0F7", // lighter shade of primary
              color: theme.palette.text.primary,
              textAlign: 'center',
              borderRadius: 2,
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              {displayProfiles.length > PROFILES_PER_PAGE ? (
                <>Showing profiles {startIndex + 1} to {endIndex} of {displayProfiles.length} (Page {currentPage} of {totalPages})</>
              ) : (
                <>Showing all {displayProfiles.length} profiles</>
              )}
            </Typography>
          </Paper>
        )}

        <ProfileList
          profiles={paginatedProfiles}
          isLoading={isLoading}
        />

        {/* Pagination Controls - FIXED: Added proper condition and styling */}
        {displayProfiles.length > PROFILES_PER_PAGE && (
          <Paper
            elevation={3}
            sx={{
              p: 3,
              mt: 4,
              mb: 4,
              borderRadius: 2,
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body1" color="text.secondary">
                Page {currentPage} of {totalPages}
              </Typography>
            </Box>

            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
              siblingCount={1}
              boundaryCount={1}
              sx={{
                '& .MuiPaginationItem-root': {
                  fontSize: '1rem',
                  fontWeight: 500,
                  minWidth: 40,
                  height: 40,
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                    }
                  },
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  }
                }
              }}
            />

            <Typography variant="body2" color="text.secondary">
              {startIndex + 1}-{endIndex} of {displayProfiles.length} profiles
            </Typography>
          </Paper>
        )}

        {!isLoading && displayProfiles.length === 0 && (
          <Paper sx={{
            textAlign: 'center',
            py: 10,
            mt: 4,
            borderRadius: 3,
            backgroundColor: theme.palette.background.alt
          }}>
            <Box sx={{
              width: 120,
              height: 120,
              borderRadius: '50%',
              backgroundColor: theme.palette.common.white,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              boxShadow: `0 8px 25px ${theme.palette.action.hover}`
            }}>
              <SearchIcon sx={{
                fontSize: 60,
                color: theme.palette.text.secondary
              }} />
            </Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              No profiles found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
              {searchQuery || hasActiveFilters ?
                `We couldn't find any profiles matching your criteria. Try a different search term or adjust your filters.` :
                'There are currently no profiles available.'
              }
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SearchIcon />}
                onClick={() => searchRef.current?.focus()}
                sx={{ borderRadius: 3, px: 4 }}
              >
                Try New Search
              </Button>
              {(hasActiveFilters || searchQuery) && (
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<ClearIcon />}
                  onClick={handleClearAllFilters}
                  sx={{ borderRadius: 3, px: 4 }}
                >
                  Clear All Filters
                </Button>
              )}
              <Button
                variant="outlined"
                color="primary"
                startIcon={<TuneIcon />}
                onClick={() => setFiltersExpanded(true)}
                sx={{ borderRadius: 3, px: 4 }}
              >
                Adjust Filters
              </Button>
            </Box>
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default ProfilesPage;